/**
 * @module components/ConnectivityIndicator
 * @description Visual indicator showing online/offline connectivity status
 * and pending offline transaction count for EmberPOS.
 *
 * Displays as a small fixed badge in the bottom-left corner of the screen.
 * - Green dot + "Online" when connected
 * - Red dot + "Offline" + pending count when disconnected
 * - Blue spinner + "Syncing..." when pushing offline data
 *
 * Clicking the indicator opens a mini-panel with more details about
 * pending offline transactions.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useConnectivity } from '../hooks/useConnectivity';

export default function ConnectivityIndicator() {
  const {
    isOnline,
    pendingCount,
    pendingSummary,
    isSyncing,
    forceCheck,
  } = useConnectivity();

  const [showDetails, setShowDetails] = useState(false);
  const [recentlyOnline, setRecentlyOnline] = useState(false);
  const panelRef = useRef(null);

  // Show a brief "Back Online" flash when status changes to online
  useEffect(() => {
    if (isOnline && pendingCount === 0) {
      setRecentlyOnline(true);
      const timer = setTimeout(() => setRecentlyOnline(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [isOnline, pendingCount]);

  // Close details panel when clicking outside
  useEffect(() => {
    if (!showDetails) return;
    const handleClick = (e) => {
      if (panelRef.current && !panelRef.current.contains(e.target)) {
        setShowDetails(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showDetails]);

  // Do not render when online with no pending items and not recently changed
  if (isOnline && pendingCount === 0 && !recentlyOnline && !isSyncing) {
    return null;
  }

  const getBadgeClasses = () => {
    if (isSyncing) return 'bg-blue-600 text-white border-blue-500';
    if (!isOnline) return 'bg-red-900 text-white border-red-600';
    if (pendingCount > 0) return 'bg-yellow-900 text-yellow-100 border-yellow-600';
    if (recentlyOnline) return 'bg-green-900 text-green-100 border-green-600';
    return 'bg-gray-800 text-gray-300 border-gray-600';
  };

  const getDotClasses = () => {
    if (isSyncing) return 'bg-blue-400 animate-pulse';
    if (!isOnline) return 'bg-red-500 animate-pulse';
    if (pendingCount > 0) return 'bg-yellow-400';
    return 'bg-green-500';
  };

  const getStatusText = () => {
    if (isSyncing) return 'Syncing...';
    if (!isOnline) return 'Offline';
    if (pendingCount > 0) return `Syncing ${pendingCount}`;
    if (recentlyOnline) return 'Back Online';
    return 'Online';
  };

  return (
    <div
      ref={panelRef}
      className="fixed bottom-4 left-4 z-[90]"
      role="status"
      aria-live="polite"
    >
      {/* Details Panel */}
      {showDetails && (
        <div className="mb-2 bg-gray-900 border border-gray-700 rounded-lg shadow-2xl p-4 w-72 animate-fade-in">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-bold text-white">
              Connection Status
            </h3>
            <button
              onClick={() => forceCheck()}
              className="text-xs text-blue-400 hover:text-blue-300 transition-colors focus:outline-none focus:ring-1 focus:ring-blue-400 rounded px-2 py-1"
              aria-label="Retry connection check"
            >
              Retry
            </button>
          </div>

          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between">
              <span className="text-gray-400">Server</span>
              <span className={isOnline ? 'text-green-400' : 'text-red-400'}>
                {isOnline ? 'Connected' : 'Unreachable'}
              </span>
            </div>

            {pendingCount > 0 && (
              <>
                <div className="border-t border-gray-700 pt-2 mt-2">
                  <p className="text-gray-400 text-xs mb-2">
                    Pending Offline Transactions
                  </p>
                  {pendingSummary.orders > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">Orders</span>
                      <span className="text-yellow-400 font-mono">
                        {pendingSummary.orders}
                      </span>
                    </div>
                  )}
                  {pendingSummary.payments > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">Payments</span>
                      <span className="text-yellow-400 font-mono">
                        {pendingSummary.payments}
                      </span>
                    </div>
                  )}
                  {pendingSummary.updates > 0 && (
                    <div className="flex items-center justify-between">
                      <span className="text-gray-300">Updates</span>
                      <span className="text-yellow-400 font-mono">
                        {pendingSummary.updates}
                      </span>
                    </div>
                  )}
                </div>
                <p className="text-gray-500 text-xs mt-2">
                  {isOnline
                    ? 'Will sync automatically...'
                    : 'Transactions saved locally. Will sync when connection resumes.'}
                </p>
              </>
            )}

            {isSyncing && (
              <div className="flex items-center gap-2 text-blue-400 mt-2">
                <div
                  className="w-3 h-3 border-2 border-blue-400 border-t-transparent rounded-full animate-spin"
                  aria-hidden="true"
                />
                <span className="text-xs">Syncing offline data...</span>
              </div>
            )}

            {isOnline && pendingCount === 0 && !isSyncing && (
              <p className="text-green-400 text-xs mt-1">
                All transactions synced.
              </p>
            )}
          </div>
        </div>
      )}

      {/* Badge */}
      <button
        onClick={() => setShowDetails(!showDetails)}
        className={`
          flex items-center gap-2 px-3 py-2 rounded-lg border
          shadow-lg transition-all duration-300 hover:scale-105
          focus:outline-none focus:ring-2 focus:ring-white/30
          ${getBadgeClasses()}
        `}
        aria-label={`Connection status: ${getStatusText()}${
          pendingCount > 0
            ? `. ${pendingCount} offline transactions pending.`
            : ''
        }`}
        aria-expanded={showDetails}
      >
        {/* Status dot */}
        <span
          className={`w-2.5 h-2.5 rounded-full ${getDotClasses()}`}
          aria-hidden="true"
        />

        {/* Label */}
        <span className="text-xs font-semibold whitespace-nowrap">
          {getStatusText()}
        </span>

        {/* Pending count badge */}
        {pendingCount > 0 && !isSyncing && (
          <span
            className="bg-red-600 text-white text-[10px] font-bold rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1"
            aria-hidden="true"
          >
            {pendingCount}
          </span>
        )}
      </button>
    </div>
  );
}
